import React, { useEffect, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import AutoSizer from 'react-virtualized-auto-sizer';
import { makeStyles } from '@material-ui/core/styles';
import { Table, TableBody, TableHead, TableRow } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import cx from 'classnames';

import { cleanupVirtualizedListCache } from '~/ducks/virtualizedList';
import Row from './Row';
import Cell from './Cell';
import { useDebouncedCallback } from 'use-debounce/lib';

export type Align = 'inherit' | 'left' | 'center' | 'right' | 'justify';

export type Column<RowData> = {
  header: string;
  selectCellData: (rowData: RowData) => any;
  id?: string; // used to determine particular column by cellDataWrapperComponent
  cellDataWrapperComponent?: any;
  headerAlign?: Align;
  cellAlign?: Align;
  style?: React.CSSProperties;
  width?: number;
};

type LoadMore = (startIndex: number, stopIndex: number) => Promise<any> | null;

interface Props<RowData> {
  resource: string;
  data: RowData[];
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  loadNextPage: () => any;
  filters?: {
    [x: string]: any;
  };
  columns: Column<RowData>[];
  emptyText?: string;
  className?: string;
  onRowClick?: (data: RowData) => any;
  outterTable?: boolean;
  rowHeight?: number;
  getRowLink?: (data: RowData) => string | undefined;
  openLinkInNewTab?: boolean;
  // AutoSizer measures this container's own rendered height, not its parent's
  // intended height - so a minHeight floor here can force the container (and
  // the whole table) taller than the space its parent actually gives it,
  // pushing content past a parent's own scroll/overflow boundary instead of
  // scrolling inside it. Defaults to 500 for consumers that want a sensible
  // minimum when their parent doesn't already provide a real height budget;
  // pass 0 when the parent's height should be respected exactly.
  minHeight?: number;
}

function VirtualizedList<RowData = any>(props: Props<RowData>) {
  const {
    resource,
    data,
    hasNextPage,
    loadNextPage,
    filters,
    columns,
    emptyText,
    onRowClick,
    outterTable = false,
    className = '',
    rowHeight = 37,
    getRowLink,
    openLinkInNewTab,
    minHeight = 500,
  } = props;

  const dispatch = useDispatch();
  const classes = useStyles();

  // We create a reference for the InfiniteLoader.
  const infiniteLoaderRef = useRef<InfiniteLoader>(null);
  const hasMountedRef = useRef(false);

  // react-window's vertical List always sizes its outer scroll div and
  // every row to exactly the `width` we pass in (100% internally) - a row
  // can never grow wider than that, so it can't horizontally overflow on
  // its own. With many fixed-width columns, the natural content width can
  // exceed the space AutoSizer measures, so columns get squeezed/clipped
  // instead of the table scrolling. Give List the full natural content
  // width (so nothing is squeezed) and let a single scrolling wrapper
  // around the whole table - header row included - handle horizontal
  // overflow, so header and body always scroll together natively.
  const contentWidth = columns.reduce(
    (sum, column) => sum + (column.width || (column.style?.flexBasis as number) || 0),
    0
  );

  // Each time the filters changed we called the method resetloadMoreItemsCache
  // to clear the cache and refetch data with applied filters.
  useEffect(() => {
    if (filters && hasMountedRef.current) {
      if (infiniteLoaderRef.current) {
        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
      }
    }
    hasMountedRef.current = true;
  }, [filters]);

  useEffect(() => {
    const current = infiniteLoaderRef.current;
    return () => {
      if (current) {
        current.resetloadMoreItemsCache(true);
      }
      if (resource) {
        dispatch(cleanupVirtualizedListCache({ resource }));
      }
    };
  }, [dispatch, resource]);

  // Must be greater than 1 cause it's source of bug: loadNextPage will never be called
  const loadIndicatorRowsCount = 12;
  // If there are more data to be loaded then add an extra row to hold a loading indicator.
  const itemCount: number = hasNextPage ? data.length + loadIndicatorRowsCount : data.length;

  // Only load 1 page of data at a time.
  // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
  // Following optimisation is cause of internal react-window-infinite-loader bug. https://youtrack.veengu.org/issue/CORE-74#focus=streamItem-4-202.0-0
  //const loadMoreItems = isNextPageLoading ? {} : loadNextPage;
  const loadMoreItems = useDebouncedCallback(loadNextPage, 0);

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = (index: number): boolean => !hasNextPage || index < data.length;

  return (
    <div className={cx(classes.container, className)} style={{ minHeight }}>
      <AutoSizer>
        {({ width, height }) => {
          // Never let the table get narrower than its columns need - once the
          // available space can't fit them, scroll horizontally instead of
          // squeezing/clipping columns.
          const tableWidth = Math.max(width, contentWidth);
          return (
            <div className={classes.scrollWrapper} style={{ width, height }}>
              <Table
                className={cx(classes.root, { [classes.outterTable]: outterTable })}
                component='div'
                style={{ width: tableWidth }}
              >
                <TableHead className={classes.head} component='div'>
                  <TableRow className={classes.rowHead} component='div'>
                    {columns.map((column, i) => {
                      return (
                        <Cell key={i} align={column.headerAlign} column={column}>
                          {column.header}
                        </Cell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody
                  component='div'
                  className={classes.body}
                  style={{
                    width: tableWidth,
                    height: height - rowHeight,
                  }}
                >
                  <InfiniteLoader
                    ref={infiniteLoaderRef}
                    isItemLoaded={isItemLoaded}
                    itemCount={itemCount}
                    loadMoreItems={loadMoreItems as LoadMore}
                  >
                    {({ onItemsRendered, ref }) => (
                      <List
                        height={height - rowHeight}
                        itemSize={rowHeight}
                        width={tableWidth}
                        onItemsRendered={onItemsRendered}
                        ref={ref}
                        // If List receives 0 after data has fetched, it will never be updated
                        itemCount={itemCount || 1}
                        style={{
                          overflowY: 'scroll',
                          overflowX: 'hidden',
                        }}
                      >
                        {({ index, style }) => (
                          <Row
                            columns={columns}
                            index={index}
                            style={style}
                            data={data}
                            isItemLoaded={isItemLoaded}
                            loadIndicatorRowsCount={loadIndicatorRowsCount}
                            emptyText={emptyText}
                            onRowClick={onRowClick}
                            getRowLink={getRowLink}
                            openLinkInNewTab={openLinkInNewTab}
                          />
                        )}
                      </List>
                    )}
                  </InfiniteLoader>
                </TableBody>
              </Table>
            </div>
          );
        }}
      </AutoSizer>
    </div>
  );
}

const useStyles = makeStyles((theme) => {
  return {
    container: {
      height: '100%',
      minWidth: '100%',
    },
    scrollWrapper: {
      overflowX: 'auto',
      overflowY: 'hidden',
    },
    root: {},
    body: {
      display: 'flex',
    },
    cellHead: {},
    head: {},
    rowHead: {
      display: 'flex',
      flexDirection: 'row',
      // List's body always reserves a vertical scrollbar's width
      // (overflowY: 'scroll' below), shrinking its visible content area
      // versus the header, which has no scrollbar of its own. Reserve the
      // same gutter here so columns start at the same x-offset in both.
      overflow: 'hidden',
      scrollbarGutter: 'stable',
    },
    outterTable: {
      backgroundColor: (theme.palette.background as any).outterTable,
    },
  };
});

export default VirtualizedList;
